import type { ReactNode } from "react";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  ClockIcon,
  GiftIcon,
  MoneyWavyIcon,
  QuestionIcon,
} from "@phosphor-icons/react";

import { Button, Text } from "@alpacahq/ui/old";

import { SealCheckIcon } from "~/v2/components/common";
import { ALGO_TRADER_PLUS_BENEFITS } from "../../const";

export type CancelReason =
  | "not_enough_value"
  | "want_to_take_break"
  | "not_satisfied"
  | "something_else";

export interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: CancelReason, reasonText?: string) => void;
  step: 1 | 2;
  selectedReason: CancelReason | null;
  onReasonChange: (reason: CancelReason) => void;
  reasonText: string;
  onReasonTextChange: (text: string) => void;
  onContinueToStep2: () => void;
  onBackToStep1: () => void;
  isCanceling: boolean;
}

const CANCEL_REASONS: {
  value: CancelReason;
  label: string;
  icon: ReactNode;
}[] = [
  {
    value: "not_enough_value",
    label: "Not enough value",
    icon: <MoneyWavyIcon size={20} />,
  },
  {
    value: "want_to_take_break",
    label: "Want to take a break",
    icon: <ClockIcon size={20} />,
  },
  {
    value: "not_satisfied",
    label: "Not satisfied with Algo Trader Plus benefits",
    icon: <GiftIcon size={20} />,
  },
  {
    value: "something_else",
    label: "Something else.",
    icon: <QuestionIcon size={20} />,
  },
];

export const CancelModal = ({
  isOpen,
  onClose,
  onConfirm,
  step,
  selectedReason,
  onReasonChange,
  reasonText,
  onReasonTextChange,
  onContinueToStep2,
  onBackToStep1,
  isCanceling,
}: CancelModalProps) => {
  const handleConfirm = () => {
    if (selectedReason) {
      onConfirm(selectedReason, reasonText || undefined);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 duration-300 ease-out data-closed:opacity-0"
      />
      <div className="fixed inset-0 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center">
          <DialogPanel
            transition
            className="flex w-full max-w-lg flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-lg duration-300 ease-out data-closed:scale-95 data-closed:opacity-0"
          >
            <DialogTitle className="flex flex-col gap-1">
              <p className="m-0 p-0 font-mono text-gray-800 text-sm">
                Step {step} <span className="text-gray-400">of 2</span>
              </p>
              <h2 className="m-0 p-0 font-semibold text-gray-800 text-lg">
                {step === 1
                  ? "Why do you want to cancel your membership?"
                  : "Are you sure you want to cancel?"}
              </h2>
            </DialogTitle>

            {step === 1 ? (
              <>
                <p className="m-0 p-0 text-gray-700 text-sm">
                  We'll use this to improve Algo Trader.
                </p>
                <div className="flex flex-col gap-3">
                  {CANCEL_REASONS.map((reason) => (
                    <label
                      key={reason.value}
                      className="flex cursor-pointer items-center gap-1"
                    >
                      <input
                        type="radio"
                        name="cancelReason"
                        value={reason.value}
                        checked={selectedReason === reason.value}
                        onChange={() => onReasonChange(reason.value)}
                        className="h-4 w-4 text-indigo-600"
                      />
                      <div className="flex flex-1 items-center gap-0.5">
                        <div>{reason.icon}</div>
                        <span className="font-medium text-gray-700 text-sm">
                          {reason.label}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                {selectedReason === "something_else" && (
                  <textarea
                    placeholder="Tell us more (optional)"
                    value={reasonText}
                    onChange={(e) => onReasonTextChange(e.target.value)}
                    rows={3}
                    className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-gray-800 text-sm placeholder-gray-500 outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400"
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col gap-4 rounded-lg border border-yellow-600 bg-[linear-gradient(106deg,rgba(255,252,216,0.65)_-38.69%,#FFF_97.09%)] p-4">
                <p className="m-0 p-0 text-gray-700 text-sm">
                  If you cancel now you will immediately be downgraded to the
                  free plan and lose all the benefits.
                </p>
                {ALGO_TRADER_PLUS_BENEFITS.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-2">
                    <SealCheckIcon />
                    <Text className="m-0 p-0 text-gray-800 text-sm">
                      {benefit}
                    </Text>
                  </div>
                ))}
              </div>
            )}

            {step === 1 ? (
              <div className="flex w-full flex-col gap-2">
                <Button
                  variant="v2/filled"
                  size="v2/base"
                  onClick={onClose}
                  className="w-full"
                >
                  Keep Algo Trader Plus
                </Button>
                <Button
                  variant="v2/ghost"
                  size="v2/base"
                  onClick={onContinueToStep2}
                  disabled={!selectedReason}
                  className="w-full"
                >
                  Continue to cancel
                </Button>
              </div>
            ) : (
              <div className="flex w-full flex-col gap-2">
                <Button
                  variant="v2/filled"
                  size="v2/base"
                  onClick={handleConfirm}
                  disabled={isCanceling}
                  loading={isCanceling}
                  className="w-full"
                >
                  Yes, cancel subscription
                </Button>
                <Button
                  type="button"
                  onClick={onBackToStep1}
                  variant="v2/ghost"
                  size="v2/base"
                  className="w-full"
                >
                  Keep Algo Trader Plus
                </Button>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
